import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const CustomAgreementGenerator = () => {
  const [formData, setFormData] = useState({
    sellerName: "",
    buyerName: "",
    propertyAddress: "",
    currentBalance: "",
    purchaseAmount: "28200",
    agreementDate: new Date().toISOString().split('T')[0],
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateAgreementContent = () => {
    const { sellerName, buyerName, propertyAddress, currentBalance, purchaseAmount, agreementDate } = formData;
    const formattedDate = new Date(agreementDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const formattedBalance = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(currentBalance));
    const formattedAmount = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(purchaseAmount));

    return `Commitment to Purchase Agreement

This Purchase Agreement made and entered into ${formattedDate},
By and between, ${sellerName} and ${buyerName}, whereas SELLER is the owner of a certain Land Contract.
The property address is: ${propertyAddress}
SELLER agrees to transfer said Land Contract, all upon the following Terms and Conditions as set forth herein: Now therefore, for and in consideration of the value to be paid, mutual benefits derived, the SELLER agrees to grant the PURCHASER and/or assigns the exclusive right to purchase the note and security instrument, and the parties hereto agree to as follows:

1. PURCHASE PRICE: SELLER warrants and represents that the balance due on the above mentioned Land Contract as of ${formattedDate} is ${formattedBalance}.

2. PURCHASER agrees to buy the entire remaining balance of ${formattedBalance} and shall pay to SELLER ${formattedAmount} for the full purchase of said Land Contract. Payment of ${formattedAmount} will be made at the time of closing of this Purchase Agreement, from escrow containing the ${formattedAmount} previously forwarded to the Title Company.

3. RETENTION OF PAYMENTS: SELLER retains the two payments for November 2025 and December 2025 in their entirety.

4. ASSUMPTION OF RESPONSIBILITIES: The PURCHASER assumes all of the responsibilities set forth within the Land Contract, effective upon closing and receipt of payment as specified in Section 2 above.

5. COSTS: PURCHASER will pay title and appraisal costs.

6. SELLER warrants that all real property taxes that are due and payable have been paid.

7. CONTINGENT: This purchase shall be expressly contingent upon the following Conditions:
A. The real property securing the above described Land Contract shall be of sufficient value to secure the investment. The determination of sufficient value shall be at the sole discretion of PURCHASER.
B. PURCHASER shall have 30 days to fully examine the SELLER'S title, the Land Contract, the CONTRACTED property, payment history, credit and financial status of the payers.

8. If any of the above mentioned conditions of sale are found to be unacceptable to PURCHASER, the PURCHASER shall notify SELLER and this contract shall become null and void.

9. SELLER shall keep and maintain documentation of any Land Contract payments that are paid during the processing of this transaction.

10. This agreement shall be in full force and effect for a period of 30 days from the date of the PURCHASER receiving all of the documentation requested.


__________________________________    __________________________________________
${sellerName}                            ${buyerName}`;
  };

  const handlePreview = () => {
    const content = generateAgreementContent();
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Custom Agreement</title>
          <style>
            body {
              font-family: 'Times New Roman', Times, serif;
              line-height: 1.6;
              max-width: 8.5in;
              margin: 0.5in auto;
              padding: 1in;
              color: #000;
            }
            h1 {
              text-align: center;
              font-size: 18pt;
              margin-bottom: 30px;
              text-decoration: underline;
            }
            p {
              margin: 15px 0;
              text-align: justify;
            }
            .section {
              margin: 20px 0;
            }
            .signature-block {
              margin-top: 50px;
            }
            @media print {
              body { margin: 0; padding: 1in; }
            }
          </style>
        </head>
        <body>
          <pre style="font-family: 'Times New Roman', Times, serif; white-space: pre-wrap;">${content}</pre>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleCopyToClipboard = () => {
    const content = generateAgreementContent();
    navigator.clipboard.writeText(content);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom Agreement Generator</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="sellerName">Seller Name</Label>
            <Input
              id="sellerName"
              value={formData.sellerName}
              onChange={(e) => handleInputChange("sellerName", e.target.value)}
              placeholder="Enter seller name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="buyerName">Purchaser Name</Label>
            <Input
              id="buyerName"
              value={formData.buyerName}
              onChange={(e) => handleInputChange("buyerName", e.target.value)}
              placeholder="Enter purchaser name"
            />
          </div>

          <div className="space-y-2 col-span-2">
            <Label htmlFor="propertyAddress">Property Address</Label>
            <Input
              id="propertyAddress"
              value={formData.propertyAddress}
              onChange={(e) => handleInputChange("propertyAddress", e.target.value)}
              placeholder="Enter property address"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="currentBalance">Current Balance</Label>
            <Input
              id="currentBalance"
              value={formData.currentBalance}
              onChange={(e) => handleInputChange("currentBalance", e.target.value)}
              placeholder="Enter current balance"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="purchaseAmount">Purchase Amount</Label>
            <Input
              id="purchaseAmount"
              value={formData.purchaseAmount}
              onChange={(e) => handleInputChange("purchaseAmount", e.target.value)}
              placeholder="28200"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="agreementDate">Agreement Date</Label>
            <Input
              id="agreementDate"
              type="date"
              value={formData.agreementDate}
              onChange={(e) => handleInputChange("agreementDate", e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Preview</Label>
          <Textarea
            value={generateAgreementContent()}
            readOnly
            className="font-mono text-sm h-96"
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={handlePreview}>
            Preview & Print
          </Button>
          <Button onClick={handleCopyToClipboard} variant="outline">
            Copy to Clipboard
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CustomAgreementGenerator;
